// The SECOND sign-in (#607 D1, option A): Google against project syntaxeum-identity, as a separate, named Firebase app
// instance used only to mint the ID token the quote callables verify (`req.data.idToken`, commerce.js `identityAuth`).
// The console's own sign-in (kit/auth.js, syntaxeum-control-plane) is untouched and never shares this instance.
//
// Nothing here runs at import. The app instance is created the first time getIdentityIdToken() is called, which the
// Quotes panel does only when the operator asks to open it. Google provider only: operator identity stays on the one
// verifier whose addresses we control (an Entra token can carry any address its directory admin chooses).
//
// HOW THE GOOGLE CREDENTIAL IS OBTAINED (#607, Mark's iPad). Firebase's signInWithPopup/Redirect keep their state in
// sessionStorage and an iframe on the cross-site auth domain; WebKit (every iOS/iPadOS browser) may partition that, and
// the pop-up ends on "missing initial state" (Firebase: "Best practices for signInWithRedirect on browsers that block
// third-party storage"; the popup flow shares the handler). This is a HYPOTHESIS until run on a real device.
//
//   * Desktop (anything that is not iPadOS/iOS WebKit): the Firebase pop-up, exactly as before. Whether or not the
//     console origins are authorised on the Google OAuth client changes nothing there.
//   * iPadOS/iOS: the credential comes from Google Identity Services in this page (a postMessage pop-up, no storage
//     handoff) and goes to Firebase through signInWithCredential. It needs the console origins under the OAuth client's
//     "Authorized JavaScript origins"; when they are missing, or GIS fails for any reason other than the operator
//     closing the window, the error carries fallbackToPopup and the panel shows a "Try the other sign-in" button that
//     calls getIdentityIdToken({ popup: true }) from a fresh tap. We never open a pop-up after an async failure (iOS
//     blocks it).
// An access-token credential is weaker than an ID-token one (Firebase can only check it through Google's tokeninfo);
// the server's operator email check (PO_OPERATORS) is what bounds it. Follow-up: google.accounts.id + credential(idToken).

import { initializeApp, getApps } from "firebase/app";
import { initializeAuth, getAuth, inMemoryPersistence, browserPopupRedirectResolver, GoogleAuthProvider, signInWithPopup, signInWithCredential, signOut } from "firebase/auth";
import { IDENTITY_FIREBASE_CONFIG, IDENTITY_GOOGLE_CLIENT_ID } from "../console/config.js";

export const IDENTITY_APP_NAME = "identity-quotes";
export const GIS_SRC = "https://accounts.google.com/gsi/client";
const GIS_SCOPE = "openid email profile";
const DEFAULT_LOGIN_HINT = "mark@syntaxeum.com"; // the only PO operator (PO_OPERATORS); used when the console knows no email
const SAFETY_MS = 120000; // a held token is dropped this long before its own exp

// ONE SIGN-IN PER CONSOLE SESSION (Mark, 2026-11-02). The ID token is held in MEMORY ONLY, in this module, for the life of
// the open tab: never localStorage, sessionStorage, IndexedDB or a cookie, never rendered. It is gone when the tab closes,
// when the console signs out, when it nears expiry (exp minus SAFETY_MS, then refreshed silently from the in-memory
// session, and only if that fails a new sign-in), and when a callable refuses it. The Firebase instance below uses
// in-memory persistence so the SDK writes nothing to IndexedDB either. Google gets the console operator's email as a
// login hint and no `prompt`, so a hinted account with a Google session is signed in without the account chooser; the
// chooser (prompt: select_account) is shown only when the operator asks for another account.
let held = null; // { token, expMs }
let loginHintSource = null;

/** The console's own sign-in registers where to read the operator email (avoids an import cycle with kit/auth.js). */
export function setLoginHintSource(fn) {
  loginHintSource = typeof fn === "function" ? fn : null;
}

/** Drops the held token (not the session). Sync, never throws. */
export function forgetIdentityToken() {
  held = null;
}

function jwtExpiryMs(token) {
  try {
    const part = String(token).split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const exp = JSON.parse(atob(part)).exp;
    return Number.isFinite(exp) ? exp * 1000 : null;
  } catch {
    return null;
  }
}

function hold(token) {
  const expMs = jwtExpiryMs(token);
  held = expMs ? { token, expMs } : null; // a token whose exp cannot be read is never held; the session answers each call
  return token;
}

function hintsFor({ chooser, loginHint }) {
  // hd narrows the chooser; a hint only. The server's PO_OPERATORS check is the gate.
  const h = { hd: "syntaxeum.com", login_hint: loginHint };
  if (chooser) h.prompt = "select_account";
  return h;
}

function resolveLoginHint(explicit) {
  if (explicit) return explicit;
  try {
    return loginHintSource?.() || DEFAULT_LOGIN_HINT;
  } catch {
    return DEFAULT_LOGIN_HINT;
  }
}

function existingApp() {
  return getApps().find((a) => a.name === IDENTITY_APP_NAME) || null;
}

function identityApp() {
  return existingApp() || initializeApp(IDENTITY_FIREBASE_CONFIG, IDENTITY_APP_NAME);
}

let identityAuthInstance = null;
let identityAuthApp = null;
/** In-memory persistence: the SDK keeps the session in this tab's memory and writes nothing to IndexedDB or storage. */
function identityAuth() {
  const app = identityApp();
  if (!identityAuthInstance || identityAuthApp !== app) {
    identityAuthApp = app;
    try {
      identityAuthInstance = initializeAuth(app, { persistence: inMemoryPersistence, popupRedirectResolver: browserPopupRedirectResolver });
      // initializeAuth (unlike getAuth) installs no resolver, so without it signInWithPopup throws auth/argument-error.
    } catch {
      identityAuthInstance = getAuth(app); // already initialized elsewhere
      Promise.resolve(identityAuthInstance.setPersistence?.(inMemoryPersistence)).catch(() => {});
    }
  }
  return identityAuthInstance;
}

let gisLoad = null;

/** Loads Google Identity Services once. Resolves to window.google.accounts.oauth2, or null when it cannot load. */
export function loadGis({ doc = typeof document !== "undefined" ? document : null, win = typeof window !== "undefined" ? window : null } = {}) {
  if (win?.google?.accounts?.oauth2) return Promise.resolve(win.google.accounts.oauth2);
  if (!doc || !win) return Promise.resolve(null);
  if (!gisLoad) {
    gisLoad = new Promise((resolve) => {
      const s = doc.createElement("script");
      s.src = GIS_SRC;
      s.async = true;
      s.onload = () => resolve(win.google?.accounts?.oauth2 || null);
      s.onerror = () => { gisLoad = null; resolve(null); };
      doc.head.appendChild(s);
    });
  }
  return gisLoad;
}

/**
 * Call when the operator reaches a screen that will need the second sign-in, so the script is in place before the
 * click: a pop-up opened after a network wait is no longer inside the tap and iOS blocks it. Never throws, and
 * creates no Firebase app (that waits for the operator's button).
 */
export function preloadIdentitySignIn() {
  if (!IDENTITY_GOOGLE_CLIENT_ID || !isIosWebKit()) return; // desktop never loads Google's script
  loadGis().catch(() => {});
}

/** True on iPadOS/iOS WebKit, including the iPad that reports itself as a Mac (MacIntel with a touch screen) and the
 *  iOS builds of Chrome/Firefox/Edge (CriOS/FxiOS/EdgiOS), which are all WebKit underneath. */
export function isIosWebKit(nav = typeof navigator !== "undefined" ? navigator : null) {
  if (!nav) return false;
  const ua = String(nav.userAgent || "");
  if (/iPad|iPhone|iPod|CriOS|FxiOS|EdgiOS/.test(ua)) return true;
  return nav.platform === "MacIntel" && Number(nav.maxTouchPoints) > 1;
}

const FALLBACK_TEXT = "Google sign-in could not finish in this browser. Use the other sign-in";

function gisError(type, message, { fallback = true } = {}) {
  const err = new Error(message);
  err.code = `auth/${type}`;
  if (fallback) err.fallbackToPopup = true;
  return err;
}

/** One GIS access-token request. Rejects with an auth/* error the panel can show; resolves to the access token. */
function requestGoogleAccessToken(oauth2, clientId, hints) {
  return new Promise((resolve, reject) => {
    const client = oauth2.initTokenClient({
      client_id: clientId,
      scope: GIS_SCOPE,
      hd: hints.hd,
      hint: hints.login_hint,
      ...(hints.prompt ? { prompt: hints.prompt } : {}),
      callback: (resp) => {
        if (resp?.access_token) resolve(resp.access_token);
        else reject(gisError("google-sign-in-failed", FALLBACK_TEXT));
      },
      error_callback: (e) => {
        if (e?.type === "popup_closed") reject(gisError("popup-closed-by-user", "The sign-in window was closed before it finished.", { fallback: false }));
        else if (e?.type === "popup_failed_to_open") reject(gisError("popup-blocked", FALLBACK_TEXT));
        else reject(gisError("google-sign-in-failed", FALLBACK_TEXT));
      },
    });
    client.requestAccessToken();
  });
}

/**
 * An ID token for the identity project. Reuses the token held in memory while it is comfortably unexpired; refreshes it
 * silently from the in-memory session when it is not; otherwise signs in with Google, which must then be called from a
 * click (a pop-up no gesture opened is blocked by the browser).
 * `popup: true` forces the Firebase pop-up (the "other sign-in" button). `chooser: true` shows Google's account chooser.
 */
export async function getIdentityIdToken({ popup = false, chooser = false, loginHint, clientId = IDENTITY_GOOGLE_CLIENT_ID, gis = loadGis, nav, now = Date.now } = {}) {
  if (held && held.expMs - SAFETY_MS > now()) return held.token;
  const wasHeld = Boolean(held);
  held = null;
  const auth = identityAuth();
  if (typeof auth.authStateReady === "function") await auth.authStateReady();
  if (auth.currentUser) {
    try {
      return hold(await auth.currentUser.getIdToken(wasHeld)); // force a refresh only when our margin said it was stale
    } catch {
      // The session could not refresh silently: fall through to a new sign-in.
    }
  }
  const hints = hintsFor({ chooser, loginHint: resolveLoginHint(loginHint) });
  if (!popup && clientId && isIosWebKit(nav)) {
    const oauth2 = await gis();
    if (!oauth2) throw gisError("google-sign-in-unavailable", FALLBACK_TEXT);
    const accessToken = await requestGoogleAccessToken(oauth2, clientId, hints);
    try {
      await signInWithCredential(auth, GoogleAuthProvider.credential(null, accessToken));
    } catch {
      throw gisError("google-sign-in-failed", FALLBACK_TEXT);
    }
  } else {
    const provider = new GoogleAuthProvider();
    provider.setCustomParameters(hints);
    await signInWithPopup(auth, provider);
  }
  return hold(await auth.currentUser.getIdToken());
}

/** Ends the identity session and forgets the held token (console sign-out, "Try another account", a refused token). */
export async function signOutIdentity() {
  forgetIdentityToken();
  if (existingApp()) await signOut(identityAuth());
}
